/**
 * bb-zip-lookup.js
 * ───────────────────────────────────────────────────────────────────────────
 * Location chooser modal controller (spec §6 + §2 manual override).
 *
 * Opens the chooser from the nav trigger or the banner CTA and resolves the
 * visitor's region one of two explicit ways (ipapi auto-detect stays the silent
 * default in bb-geo-detect.js):
 *   - "Use my current location" → browser geolocation (bb-browser-geo.js),
 *     persisted as a CONFIRMED state-level location (source "browser_geo").
 *   - Enter a ZIP → validated against our by-state data, persisted as a
 *     CONFIRMED location (source "user_zip") with the `State · ZIP` label.
 * Both re-run filtering + dismiss the nudge via the resolution event.
 *
 * The nav pill label is kept in sync with the stored location on every
 * resolution — including a guessed ipapi/browser result — so it never lingers
 * on "Set region" once a state is known.
 *
 * SECURITY (§0.5):
 *   - Input is sanitized/validated (sanitizeZip → ^\d{5}$) before use.
 *   - All dynamic text (errors, status, the State·ZIP label) is written via
 *     textContent, never innerHTML.
 *   - The ZIP/coordinates never enter the URL/history: the form submit is
 *     always prevented; geolocation is handled in bb-browser-geo.js.
 *   - Only the allow-listed location fields are persisted (via the location store).
 */

import { sanitizeZip, getRegionForZip, getRegionForState, getStateName, isServedRegion, preloadZipData } from '@theme/bb-region-config';
import { getLocation, setLocation } from '@theme/bb-location-store';
import { BB_LOCATION_EVENT, emitLocationResolved } from '@theme/bb-geo-detect';
import { BB_OPEN_ZIP_MODAL } from '@theme/bb-location-banner';
import { resolveByBrowserGeo } from '@theme/bb-browser-geo';

const INVALID_FORMAT_MSG = 'Please enter a 5-digit US ZIP code.';
const NOT_FOUND_MSG = "We couldn't find that ZIP. Please check and try again.";
const GEO_LOCATING_MSG = 'Locating you…';
const GEO_FAIL_MSG = {
  unsupported: "Your browser can't share location. Enter your ZIP instead.",
  denied: 'Location access was blocked. Enter your ZIP instead.',
  unavailable: "We couldn't get your location. Enter your ZIP instead.",
  not_resolved: "We couldn't match your location to a region. Try your ZIP.",
};

/**
 * Resolve { region, state, zip } from a ZIP (the availability key, status-aware)
 * with a state fallback for when the ZIP isn't in the table. A known-but-unserved
 * ZIP (status false) resolves to region null — more specific than the state, so
 * we do NOT fall back to the state's region in that case.
 */
async function resolvePlace(zip, state) {
  const match = zip ? await getRegionForZip(zip) : null; // { state, region, status } | null
  // `served` is status-aware from the ZIP table; the state fallback derives it
  // from the launch scope (region ∈ A–C). A known state outside A–C → false;
  // no state at all → null (unknown).
  if (match) return { region: match.region, state: match.state, zip, served: match.status };
  const region = state ? getRegionForState(state) : null;
  return {
    region,
    state: state || null,
    zip: zip || null,
    served: isServedRegion(region) ? true : state ? false : null,
  };
}

function boot() {
  if (typeof document === 'undefined') return;

  const dialog = document.getElementById('bb-zip-dialog');
  const form = document.getElementById('bb-zip-form');
  const input = document.getElementById('bb-zip-input');
  const errorEl = document.getElementById('bb-zip-error');
  const closeBtn = document.getElementById('bb-zip-dialog-close');
  const geoBtn = document.getElementById('bb-geo-locate');
  const geoStatus = document.getElementById('bb-geo-status');
  const detectedEl = document.getElementById('bb-loc-detected');
  // Query triggers LIVE, not once at boot: the mobile drawer pill is rendered
  // after this script boots (lazy header/section render), so a boot-time snapshot
  // would miss it and leave it stuck on "Set Your Planting Zone".
  const getTriggers = () => document.querySelectorAll('.bb-zip-trigger');

  if (!dialog || !form || !input || !errorEl) return;

  // The element that opened the dialog, so focus can return there on close.
  let opener = null;

  const setExpanded = (isOpen) => {
    for (const trigger of getTriggers()) trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
  };

  const clearError = () => {
    errorEl.textContent = ''; // empty live region — collapsed via CSS :empty
    input.removeAttribute('aria-invalid');
  };

  const showError = (message) => {
    errorEl.textContent = message; // textContent into a persistent role=alert region
    input.setAttribute('aria-invalid', 'true');
    input.focus();
  };

  const setGeoStatus = (message) => {
    if (geoStatus) geoStatus.textContent = message || '';
  };

  /** Reflect the current stored location in the modal's detected line. */
  const renderDetected = () => {
    if (!detectedEl) return;
    const loc = getLocation();
    if (!loc || !loc.state) {
      detectedEl.textContent = '';
      detectedEl.setAttribute('hidden', '');
      return;
    }
    const name = getStateName(loc.state) || loc.state;
    if (loc.region != null) {
      detectedEl.textContent =
        loc.zip_confidence === 'confirmed'
          ? `Showing availability for ${name}.`
          : `We think you're in ${name}. Confirm or change it below.`;
    } else {
      // Known location, but not in a served region (out-of-area / status:false).
      detectedEl.textContent = `We don't currently deliver to ${name}${loc.zip ? ' ' + loc.zip : ''}. Try another ZIP.`;
    }
    detectedEl.removeAttribute('hidden');
  };

  const open = () => {
    opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    preloadZipData(); // warm the ZIP table so submit/geo resolve instantly
    clearError();
    setGeoStatus('');
    input.value = '';
    renderDetected();
    if (typeof dialog.showModal === 'function') {
      if (!dialog.open) dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }
    setExpanded(true);
    document.documentElement.classList.add('bb-modal-open'); // body scroll-lock [R-10]
    requestAnimationFrame(() => {
      if (geoBtn) geoBtn.focus();
      else input.focus();
    });
  };

  const close = () => {
    if (typeof dialog.close === 'function') {
      if (dialog.open) dialog.close();
    } else {
      dialog.removeAttribute('open');
    }
    document.documentElement.classList.remove('bb-modal-open'); // release scroll-lock [R-10]
    setExpanded(false);
    if (opener && typeof opener.focus === 'function') opener.focus();
  };

  /**
   * Paint the nav pill from a known state. With a ZIP → confirmed `State · ZIP`;
   * without → a "located" state-only label (guessed ipapi / browser geo).
   */
  const updateTriggers = (state, zip) => {
    const name = getStateName(state) || state;
    const located = Boolean(state);
    for (const trigger of getTriggers()) {
      const text = trigger.querySelector('.bb-zip-trigger__text');
      const nameEl = trigger.querySelector('.bb-zip-trigger__state-name');
      const codeEl = trigger.querySelector('.bb-zip-trigger__state-code');
      const zipEl = trigger.querySelector('.bb-zip-trigger__zip');
      if (nameEl) nameEl.textContent = located ? name : '';
      if (codeEl) codeEl.textContent = located ? state : '';
      if (zipEl) zipEl.textContent = zip || '';
      if (text) {
        text.setAttribute('data-bb-confirmed', zip ? 'true' : located ? 'guessed' : 'false');
      }
      // Keep the accessible name in sync with the visible value.
      if (zip) trigger.setAttribute('aria-label', `Change ZIP code — ${name}, ${zip}`);
      else if (located) trigger.setAttribute('aria-label', `Located in ${name} — set or change your ZIP code`);
      else trigger.setAttribute('aria-label', 'Set your ZIP code for product availability');
    }
  };

  /** Pull the authoritative stored record onto the pill (ipapi/browser/zip). */
  const syncPillFromStore = () => {
    const loc = getLocation();
    // Show the visitor's location whenever we have a STATE — including an
    // UNSERVED one (region === null, e.g. an out-of-area or status:false ZIP).
    // The unmapped banner + red dot convey "we don't serve your area"; the pill
    // must still reflect the ZIP they set, not silently reset to "Set region".
    if (loc && loc.state) updateTriggers(loc.state, loc.zip);
    else updateTriggers(null, null);
  };

  const handleSubmit = async () => {
    const zip = sanitizeZip(input.value);
    if (!zip) {
      showError(INVALID_FORMAT_MSG);
      return;
    }

    const place = await getRegionForZip(zip); // { state, region, status } | null
    if (!place) {
      // Valid format but the ZIP isn't in our table at all (typo or outside our
      // coverage). Re-prompt. Spec §2.
      showError(NOT_FOUND_MSG);
      return;
    }

    // Found — either serviceable (region set) or known-but-not-serviceable
    // (region null → unmapped flow shows the "we don't serve your area" banner).
    const stored = setLocation({
      region: place.region,
      state: place.state,
      zip,
      source: 'user_zip',
      zip_confidence: 'confirmed',
      served: place.status, // status-aware: A–C ZIP → true; out-of-area ZIP → false
    });

    emitLocationResolved(stored); // re-filter + dismiss/seed banner
    updateTriggers(place.state, zip);
    close();
  };

  const handleGeolocate = async () => {
    if (!geoBtn) return;
    clearError();
    geoBtn.disabled = true;
    geoBtn.setAttribute('aria-busy', 'true');
    setGeoStatus(GEO_LOCATING_MSG);

    let result;
    try {
      result = await resolveByBrowserGeo();
    } catch (_) {
      result = { ok: false, reason: 'unavailable' };
    }

    geoBtn.disabled = false;
    geoBtn.removeAttribute('aria-busy');

    if (!result || !result.ok) {
      setGeoStatus(GEO_FAIL_MSG[result && result.reason] || GEO_FAIL_MSG.unavailable);
      input.focus();
      return;
    }

    // Resolve region by ZIP (the availability key) and fall back to the state
    // when the ZIP isn't in our served data yet. Store the ZIP either way so the
    // pill shows State · ZIP like the manual path.
    const place = await resolvePlace(result.zip, result.state);
    const stored = setLocation({
      region: place.region,
      state: place.state,
      zip: place.zip,
      source: 'browser_geo',
      zip_confidence: 'confirmed',
      served: place.served,
    });

    setGeoStatus('');
    emitLocationResolved(stored); // re-filter + dismiss nudge (banner handles unmapped)
    updateTriggers(place.state, place.zip);
    close();
  };

  // Delegated click so ANY pill opens the modal — including the drawer pill that
  // renders after boot. open() reads document.activeElement for focus-return, so
  // it needs no event target.
  document.addEventListener('click', (event) => {
    if (event.target instanceof Element && event.target.closest('.bb-zip-trigger')) open();
  });
  document.addEventListener(BB_OPEN_ZIP_MODAL, open);
  closeBtn?.addEventListener('click', close);
  geoBtn?.addEventListener('click', handleGeolocate);

  // Native <dialog> backdrop click (event target is the dialog itself).
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) close();
  });

  // Escape key closes the native dialog without calling close() — release the
  // scroll-lock here too so it can't get stuck on. [R-10]
  dialog.addEventListener('close', () => {
    document.documentElement.classList.remove('bb-modal-open');
  });

  form.addEventListener('submit', (event) => {
    event.preventDefault(); // keep the ZIP out of the URL/history
    handleSubmit();
  });

  // Keep the pill label in sync with every resolution (incl. guessed ipapi),
  // and paint it from any already-cached location on boot.
  document.addEventListener(BB_LOCATION_EVENT, syncPillFromStore);
  syncPillFromStore();

  // The mobile drawer pill is (re)rendered after boot and after the last location
  // event fires, so it would never get painted. Re-sync whenever a trigger is
  // added to the DOM so late/replaced pills reflect the stored location.
  if (typeof MutationObserver === 'function') {
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (node.matches?.('.bb-zip-trigger') || node.querySelector?.('.bb-zip-trigger')) {
            syncPillFromStore();
            return;
          }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  // The drawer is a hydrated island that morphs its DOM to match the server HTML
  // shortly after boot — which resets its pill to the "Set Your Planting Zone"
  // default even after we painted it. Re-sync whenever a <details> (the drawer or
  // Browse) toggles, so the pill reflects the stored location every time it's
  // revealed. Capture phase catches the non-bubbling toggle event; the rAF + short
  // timeout ride out any concurrent morph / section render.
  document.addEventListener(
    'toggle',
    () => {
      requestAnimationFrame(syncPillFromStore);
      setTimeout(syncPillFromStore, 250);
    },
    true
  );
}

boot();
